import React from 'react';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import FilterListView from '../../components/FilterListView';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  Bar,
  XAxis,
  YAxis,
  LabelList,
  Cell,
  Legend,
} from 'recharts';

import Scenario from '../../api/Scenario';
import Header from '../../components/utils/Header';
import Collapsible from "../../components/utils/Collapsible";
import {
  formatVolume,
  formatRatioPercent,
  unitFormatter,
  formatPercent,
} from '../../utils';
import ConditionalFormat from '../../components/utils/ConditionalFormat';
import ScenarioSelect from '../scenarios/ScenarioSelect';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import DemandSwitches from '../../store/DemandSwitches';
import DemandSwitchesPanel from '../../components/DemandSwitchesPanel';
import DataFrameDownload from '../../components/utils/DataFrameDownload';

const styles = theme => ({
  chartContainer: {
    overflow: 'hidden',
  },
  tablePaper: {
    overflow: 'auto',
  },
  tableSeparator: {
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
  subtotalColor: {
    backgroundColor: theme.palette.grey[100],
  },
  totalColor: {
    backgroundColor: theme.palette.grey[300],
  },
  autoRowSize: {
    tableLayout: 'auto',
    width: 'auto',
  },
});


@withStyles(styles)
@withRouter
@observer
class DemandBridgesView extends BaseScenarioDashboardView {
  static apiSubPath = '/demand_bridges/';

  @observable scenario = null;
  @observable year = null;

  constructor(props) {
    super(props);
    this.switches = new DemandSwitches({}, this.updateData);
  }

  _setData(data) {
    this.scenario = data.scenario;
    this.year = data.year;

    this.switches.set('Base demand in scope', data.base_demand_in_scope);
    this.switches.set('Base demand out of scope', data.base_demand_out_scope);
    this.switches.set('Innovation in scope', data.innovation_demand);
    this.switches.set('Risks & opps in scope', data.risks_opps_demand);

  }

  _getQueryParams() {
    return {
      scenario: this.scenario,
      year: this.year,

      base_demand_in_scope: this.switches.get('Base demand in scope'),
      base_demand_out_scope: this.switches.get('Base demand out of scope'),
      innovation_demand: this.switches.get('Innovation in scope'),
      risks_opps_demand: this.switches.get('Risks & opps in scope'),

    };
  }

  onScenarioChange = this.genericChangeHandler('scenario');
  onYearChange = this.genericChangeHandler('year');

  getColor(column, value) {
    if (column === "Athena (Central S&OP)") {
      return "#00a8d7";
    }
    if (column === 'Market (Local S&OP)' || column === 'GPS (Constraint sell-in)' || column === 'Rolling Forecast') {
      return "#cdcaf3";
    }
    return value > 0 ? '#32c77f' : '#fc5c65';
  };

  getTextColor(column) {
    return column === "Athena (Central S&OP)" ? "white" : "black";
  };

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          {this.renderChoicesField(
            'Year', this.data.year_choices, this.year,
            this.onYearChange,
          )}
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario"
            scenarioId={this.scenario}
            choices={this.data.scenario_choices}
            onScenarioChange={this.onScenarioChange}
          />
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl('/demand_bridges?' + this.getQueryString())}
            permission="athena.download_demand_bridges"
          />
        </Grid>
      </Grid>
    );
  }

  table(tableName) {
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }

    const data = tableName === "LVL2 Table" ? this.data.table_lv2_data : this.data.table_cbu_data;
    const indexName = tableName === "LVL2 Table" ? 'LVL2' : 'Market Name';

    const { classes } = this.props;

    if (data.length === 0) {
      return 'No data found';
    }
    const headers = Object.keys(data[0]);

    const isIndex = key => key === indexName;
    const getCellClass = key => (
      isIndex(key)
        ? classes.tableSeparator
        : null
    );
    const paintRow = (key) => {
      if (key === 'TOTAL') {
        return classes.totalColor;
      } else if (key === 'SUBTOTAL') {
        return classes.subtotalColor;
      }
      return null;
    };

    const getCellProps = key => ({
      key, variant: isIndex(key) ? 'head' : null, align: isIndex(key) ? 'left' : 'right', className: getCellClass(key),
    });

    const getValue = (row, key) => {
      const value = row[key];
      if (isIndex(key)) {
        return value;
      }
      const formattedValue = (key === 'Year' || key === 'LV2 DSC' || key === 'Market Name'
        ? row[key] : key === 'Gap Athena vs Local S&OP' ? formatPercent(row[key], 1) : formatVolume(row[key], 't', 0));

      const percentKey = key + ' (%)';
      if (percentKey in row) {
        return (
          <ConditionalFormat value={row[percentKey]} name="demand-bridges">
            {formattedValue} ({formatRatioPercent(row[percentKey])})
          </ConditionalFormat>
        );
      }
      return formattedValue;
    };
    console.log(indexName)
    return (
      <Collapsible title={tableName}>
        <Table stickyHeader size="small" className={classes.autoRowSize}>
          <TableHead>
            <TableRow>
              {headers.map(key => (
                <TableCell {...getCellProps(key)}>{key}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index} className={paintRow(row[indexName])}>
                {headers.map(key =>
                  <TableCell {...getCellProps(key)}>
                    {getValue(row, key)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapsible>
    );
  }

  get lvl2Table() {
    return this.table("LVL2 Table");
  }
  get marketTable() {
    return this.table("Market Table");
  }

  get chart() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const barGap = 4;
    var dictXY = {};

    const renderCustomizedLabel = (props) => {
      const {
        x, y, width, height, value, fill,
      } = props;

      dictXY[value.index] = { x: x, y: y };

      const deltaArrow = (
        <g>
          <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5"
              markerWidth="5" markerHeight="5"
              orient="auto-start-reverse" fill="grey">
              <path d="M 0 0 L 10 5 L 0 10 z" />
            </marker>
          </defs>
          <line strokeDasharray="4" strokeWidth="1" stroke="grey"
            x1={dictXY["Athena (Central S&OP)"].x + width / 2}
            x2={dictXY["Athena (Central S&OP)"].x + width / 2}
            y1={dictXY["Athena (Central S&OP)"].y - 0.10 * width}
            y2={Math.min(y - 0.15 * width, dictXY["Athena (Central S&OP)"].y - 0.10 * width)}
          />
          <line strokeDasharray="4" strokeWidth="1" stroke="grey"
            x1={dictXY["Athena (Central S&OP)"].x + width / 2}
            x2={x + width / 2}
            y1={Math.min(y - 0.15 * width, dictXY["Athena (Central S&OP)"].y - 0.10 * width)}
            y2={Math.min(y - 0.15 * width, dictXY["Athena (Central S&OP)"].y - 0.10 * width)}
          />
          <line strokeDasharray="4" strokeWidth="1" stroke="grey"
            x1={x + width / 2}
            x2={x + width / 2}
            y1={Math.min(y - 0.15 * width, dictXY["Athena (Central S&OP)"].y - 0.10 * width)}
            y2={y - 0.05 * width}
            markerEnd="url(#arrow)"
          />
          <text x={(dictXY["Athena (Central S&OP)"].x + x + 0.8 * width) / 2}
            y={Math.min(y - 0.2 * width, dictXY["Athena (Central S&OP)"].y - 0.15 * width)}
            fontSize={width / 8}
          >
            {formatPercent(this.data.graph_data.filter(x => x.index === 'Market (Local S&OP)')[0].delta_ratio, 1)}
          </text>
        </g>
      );

      const valueBox = (
        <g>
          <rect x={x + width / 4} y={y + height / 2 - width / 10} width={width / 2} height={width / 5} fill={fill} fillOpacity="0.4" />
          <text x={x + width / 2} y={y + height / 2} fill={this.getTextColor(value.index)} fontSize={Math.min(16, width / 8)} textAnchor="middle" dominantBaseline="middle">
            {(value.value > 0 ? '+' : '').concat(unitFormatter('t', 0)(value.value))}
          </text>
        </g>
      );

      return (
        <g>
          {valueBox}
          {value.index === "Market (Local S&OP)" ? deltaArrow : <></>}
        </g>
      );
    };

    return (
      <Paper>
        <ResponsiveContainer aspect={21 / 9}>
          <BarChart width={730} height={250} data={toJS(this.data.graph_data)} barGap={barGap}
            margin={{ top: 55, right: 30, bottom: 30, left: 30 }}>
            <CartesianGrid stroke='#f5f5f5' />
            <XAxis dataKey="index" allowDataOverflow={true} />
            <YAxis />
            <Legend verticalAlign="top" align='right' height={36} payload={[
              { value: 'Selected scope for Athena', color: this.getColor("Athena (Central S&OP)") },
              { value: 'Full scope', color: this.getColor("GPS (Constraint sell-in)") }
            ]} />
            <Bar dataKey="value_array">
              {this.data.graph_data.map((row) => (
                <Cell key={row.index} fill={this.getColor(row.index, row.value)} />
              ))}
              <LabelList dataKey={(row) => ({ value: row.value, index: row.index })} content={renderCustomizedLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Demand Bridges" />
          </Grid>
          <Grid item>
            {this.controls}
          </Grid>
        </Grid>
        <Grid item>
          <Grid item>
            <DemandSwitchesPanel switches={this.switches} />
          </Grid>
        </Grid>
        <Grid item className={classes.chartContainer}>
          {this.chart}
        </Grid>
        <Grid item>
          {this.lvl2Table}
        </Grid>
        <Grid item>
          {this.marketTable}
        </Grid>
      </Grid>
    );
  }
}

export default DemandBridgesView;
